import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { ErrorBoundary } from './components/ErrorBoundary'
import { Navbar } from './components/Navbar'
import { ProtectedRoute } from './components/ProtectedRoute'
import { ToastProvider } from './components/Toast'
import { AuthProvider } from './contexts/AuthProvider'
import { Login } from './pages/Login'
import { NotFound } from './pages/NotFound'

// Lazy-loaded pages — each becomes a separate chunk
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })))
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })))
const MeetingDetail = lazy(() => import('./pages/MeetingDetail').then(m => ({ default: m.MeetingDetail })))
const Wachtrij = lazy(() => import('./pages/Wachtrij').then(m => ({ default: m.Wachtrij })))
const Clients = lazy(() => import('./pages/Clients').then(m => ({ default: m.Clients })))
const TeamMembers = lazy(() => import('./pages/TeamMembers').then(m => ({ default: m.TeamMembers })))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const VaultEntities = lazy(() => import('./pages/VaultEntities').then(m => ({ default: m.VaultEntities })))
const WebhookEvents = lazy(() => import('./pages/WebhookEvents').then(m => ({ default: m.WebhookEvents })))

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

// Configure QueryClient with reasonable defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // Data fresh for 1 minute
      retry: 1, // Only retry once on failure
    },
  },
})

/**
 * Main app component with routing and provider nesting.
 *
 * Provider nesting order:
 * 1. QueryClientProvider - Outermost (for React Query DevTools)
 * 2. AuthProvider - Wraps Router (auth context available everywhere)
 * 3. BrowserRouter - Innermost (routes have access to all context)
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      <AuthProvider>
        <BrowserRouter>
          <ToastProvider>
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route element={<ProtectedRoute />}>
                  <Route element={<Navbar />}>
                    <Route path="/" element={<Navigate to="/wachtrij" replace />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/categorisations/:id" element={<MeetingDetail />} />
                    <Route path="/wachtrij" element={<Wachtrij />} />
                    <Route path="/wachtrij/:id" element={<Wachtrij />} />
                    <Route path="/review" element={<Navigate to="/wachtrij" replace />} />
                    <Route path="/webhooks" element={<WebhookEvents />} />
                    <Route path="/vault-entities" element={<VaultEntities />} />
                    <Route path="/clients" element={<Clients />} />
                    <Route path="/team" element={<TeamMembers />} />
                    <Route path="/settings" element={<Settings />} />
                  </Route>
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
          </ToastProvider>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
